import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Send, Check, X, RotateCcw, Trash2, Printer, Pencil, FileText, Layers, Upload,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDate, outletNames } from "../lib/format";
import { PageIntro, PanelHead, Modal, Field, SelectField, Badge, Pagination } from "../components/UI";
import { usePagination } from "../lib/usePagination";
import { ItemPicker } from "../components/ItemPicker";
import { BulkUploadDialog } from "../components/BulkUpload";
import { useAuth } from "../context/AuthContext";
import { printPurchaseRequest } from "../lib/printDocs";

const PRIORITY_TONE = { low: "neutral", medium: "blue", high: "amber", urgent: "amber" };
const STATUS_TONE = {
  draft: "neutral",
  pending_approval: "amber",
  approved: "green",
  rejected: "neutral",
  returned: "amber",
  converted: "green",
  cancelled: "neutral",
};
const STATUS_LABEL = {
  draft: "Draft",
  pending_approval: "Pending approval",
  approved: "Approved",
  rejected: "Rejected",
  returned: "Returned",
  converted: "Converted to PO",
  cancelled: "Cancelled",
};

const PR_UPLOAD_TEMPLATE = {
  headers: ["pr_ref", "outlet_code", "cost_center", "project", "priority", "required_delivery_date", "notes", "item_sku", "item_name", "category", "qty", "unit", "line_notes"],
  example: [
    ["A", "kitchen", "CC-KITCHEN-01", "Weekend menu", "high", "2026-09-05", "urgent restock", "PRT-001", "Ribeye 200g", "Protein", "20", "kg", "grade A"],
    ["A", "kitchen", "CC-KITCHEN-01", "Weekend menu", "high", "2026-09-05", "urgent restock", "VEG-001", "Romaine lettuce", "Vegetable", "10", "kg", ""],
    ["B", "bar", "CC-BAR-01", "", "medium", "", "restock", "BEV-042", "Mineral water 500ml", "Beverage", "40", "carton", ""],
  ],
};

export default function PurchaseRequestPage() {
  const { user } = useAuth();
  const isRequester = user?.role === "requester";
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [prs, setPrs] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [flow, setFlow] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // "form" | "detail" | "convert" | {decide, pr, decision}
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [convertLines, setConvertLines] = useState([]);
  const [cancelReason, setCancelReason] = useState("");
  const [outlets, setOutlets] = useState([]);

  const loadAll = () => {
    api.get("/purchase-requests").then((r) => setPrs(r.data));
  };
  useEffect(() => {
    loadAll();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/pr-config").then((r) => setFlow(r.data.approval_flow || []));
    api.get("/outlets?active=true").then((r) => setOutlets(r.data || []));
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(defaultForm());
    setModal("form");
  };

  const openEdit = (pr) => {
    setEditingId(pr.id);
    setForm({
      outlet_code: pr.outlet_code || pr.department || "",
      cost_center: pr.cost_center || "",
      requester_name: pr.requester_name || "",
      required_delivery_date: pr.required_delivery_date || "",
      project: pr.project || "",
      priority: pr.priority || "medium",
      notes: pr.notes || "",
      items: (pr.items || []).map((l) => ({
        item_id: l.item_id || "",
        sku: l.sku || "",
        name: l.name || "",
        category: l.category || "",
        qty: String(l.qty || 1),
        unit: l.unit || "pcs",
        unit_locked: !!l.item_id,
        price: l.price != null ? String(l.price) : "",
        notes: l.notes || "",
        converted: !!l.converted,
        converted_po_number: l.converted_po_number || null,
      })),
      attachments: pr.attachments || [],
    });
    setModal("form");
  };

  const setLine = (idx, patch) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, items: next });
  };
  const addLine = () =>
    setForm({ ...form, items: [...form.items, blankLine()] });
  const removeLine = (idx) =>
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const onPickItem = (idx, id) => {
    const it = items.find((i) => i.id === id);
    if (!it) {
      setLine(idx, { item_id: "", sku: "", name: "", category: "", unit: "" });
      return;
    }
    // Prefill price with the last known purchase price (still editable).
    const lastPrice = it.last_price ?? it.cost ?? 0;
    setLine(idx, {
      item_id: it.id,
      sku: it.sku || "",
      name: it.name || "",
      category: it.category || "",
      // UOM comes from the item master and is locked to prevent inconsistencies.
      unit: it.unit || "pcs",
      unit_locked: true,
      price: lastPrice ? String(lastPrice) : "",
    });
  };

  const onFilesPicked = async (fileList) => {
    const files = Array.from(fileList).slice(0, 5 - (form.attachments?.length || 0));
    const readOne = (f) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ name: f.name, size: f.size, data: reader.result });
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
    const attaches = [];
    for (const f of files) {
      if (f.size > 2 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 2MB limit`);
        continue;
      }
      attaches.push(await readOne(f));
    }
    setForm({ ...form, attachments: [...(form.attachments || []), ...attaches] });
  };

  const removeAttach = (i) =>
    setForm({ ...form, attachments: form.attachments.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    if (!form.outlet_code.trim()) return toast.error("Outlet is required");
    if (isRequester && !(form.requester_name || "").trim()) return toast.error("Nama Pemesan wajib diisi");
    const clean = form.items.filter((l) => l.name && Number(l.qty) > 0);
    if (clean.length === 0) return toast.error("Add at least 1 item");
    const missingUnit = clean.find((l) => !(l.unit || "").trim());
    if (missingUnit) return toast.error(`Unit of measure is required for "${missingUnit.name || 'each line'}"`);
    setSaving(true);
    try {
      const payload = {
        outlet_code: form.outlet_code,
        cost_center: form.cost_center,
        requester_name: form.requester_name || null,
        required_delivery_date: form.required_delivery_date || null,
        project: form.project,
        priority: form.priority,
        notes: form.notes,
        items: clean.map((l) => ({
          item_id: l.item_id || null,
          sku: l.sku,
          name: l.name,
          category: l.category,
          qty: Number(l.qty),
          unit: l.unit,
          price: l.price === "" || l.price == null ? null : Number(l.price),
          notes: l.notes,
        })),
        attachments: (form.attachments || []).map((a) => ({
          name: a.name,
          data: a.data,
          size: a.size || 0,
        })),
      };
      if (editingId) {
        await api.put(`/purchase-requests/${editingId}`, payload);
        toast.success("PR updated");
      } else {
        await api.post("/purchase-requests", payload);
        toast.success("PR saved as draft");
      }
      setModal(null);
      setEditingId(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const submit = async (pr) => {
    if (!window.confirm(`Submit ${pr.pr_number} for approval?`)) return;
    try {
      await api.post(`/purchase-requests/${pr.id}/submit`);
      toast.success(`${pr.pr_number} submitted`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openDetail = async (pr) => {
    const { data } = await api.get(`/purchase-requests/${pr.id}`);
    setDetail(data);
    setModal("detail");
  };

  const openDecide = (pr, decision) => {
    setDetail(pr);
    setComment("");
    setModal({ decide: true, pr, decision });
  };

  const submitDecision = async (e) => {
    e.preventDefault();
    const { pr, decision } = modal;
    if ((decision === "rejected" || decision === "returned") && !comment.trim()) {
      return toast.error("Comment is required");
    }
    setSaving(true);
    try {
      const endpoint =
        decision === "approved" ? "approve" : decision === "rejected" ? "reject" : "return";
      await api.post(`/purchase-requests/${pr.id}/${endpoint}`, { comment });
      toast.success(`${pr.pr_number} ${decision}`);
      setModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const openConvert = (pr) => {
    setDetail(pr);
    // Only outstanding lines are convertible; already-converted lines are locked.
    setConvertLines(
      (pr.items || []).map((l) => {
        if (l.converted) {
          return { supplier: l.converted_supplier || "", price: String(l.converted_price || 0), converted: true, converted_po_number: l.converted_po_number };
        }
        const it = items.find((i) => i.id === l.item_id);
        const prPrice = l.price != null && l.price !== "" ? Number(l.price) : null;
        const lastPrice = it?.last_price != null ? Number(it.last_price) : null;
        const price = prPrice ?? lastPrice ?? "";
        return {
          supplier: it?.last_supplier || "",
          price: price === "" ? "" : String(price),
          selected: true, // by default all outstanding lines are selected
          converted: false,
        };
      })
    );
    setModal("convert");
  };

  const submitConvert = async (e) => {
    e.preventDefault();
    const submitLines = convertLines
      .map((l, idx) => ({ ...l, line_index: idx }))
      .filter((l) => !l.converted && l.selected);
    if (submitLines.length === 0) {
      return toast.error("Pick at least one line to convert");
    }
    const bad = submitLines.find((l) => !l.supplier || Number(l.price) <= 0);
    if (bad) return toast.error("Every selected line needs a supplier and price > 0");
    setSaving(true);
    try {
      const payload = {
        lines: submitLines.map((l) => ({
          line_index: l.line_index,
          supplier: l.supplier,
          price: Number(l.price),
        })),
      };
      const { data } = await api.post(`/purchase-requests/${detail.id}/convert`, payload);
      if (data.fully_converted) {
        toast.success(`Generated ${data.count} PO(s). PR fully converted.`);
      } else {
        toast.success(`Generated ${data.count} PO(s). ${data.outstanding_lines} line(s) still outstanding.`);
      }
      setModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const doPrint = async (pr) => {
    // Fetch fresh item data so the Stock balance column shows the live
    // Main Warehouse on-hand qty at print time (not a cached figure).
    try {
      const r = await api.get("/items");
      printPurchaseRequest(pr, r.data || []);
    } catch {
      printPurchaseRequest(pr, items);
    }
  };

  const currentApproverRole = (pr) => {
    const f = pr.approval_flow || [];
    const lvl = pr.current_level || 0;
    return f[lvl] || null;
  };

  const canApprove = (pr) => {
    if (pr.status !== "pending_approval") return false;
    const role = currentApproverRole(pr);
    return user?.role === role || user?.role === "admin";
  };
  const canEditPr = (pr) => {
    if (["converted", "cancelled", "rejected"].includes(pr.status)) return false;
    if (user?.role === "admin") return true;
    return (
      (pr.status === "draft" || pr.status === "returned") &&
      pr.requester_username === user?.username
    );
  };
  const canSubmit = (pr) =>
    (pr.status === "draft" || pr.status === "returned") &&
    (pr.requester_username === user?.username || user?.role === "admin");
  const canConvert = (pr) =>
    pr.status === "approved" && ["admin", "purchasing"].includes(user?.role);
  const canCancel = (pr) =>
    user?.role === "admin" &&
    pr.status !== "cancelled" &&
    pr.status !== "converted";

  const openCancel = (pr) => {
    setDetail(pr);
    setCancelReason("");
    setModal({ cancel: true, pr });
  };

  const submitCancel = async (e) => {
    e.preventDefault();
    const reason = cancelReason.trim();
    if (!reason) return toast.error("Cancellation reason is required");
    setSaving(true);
    try {
      await api.post(`/purchase-requests/${modal.pr.id}/cancel`, { reason });
      toast.success(`${modal.pr.pr_number} cancelled`);
      setModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = statusFilter ? prs.filter((p) => p.status === statusFilter) : prs;
    if (!q) return list;
    return list.filter(
      (p) =>
        p.pr_number.toLowerCase().includes(q) ||
        (p.requester_name || "").toLowerCase().includes(q) ||
        (p.outlet_code || p.department || "").toLowerCase().includes(q) ||
        (p.status || "").toLowerCase().includes(q)
    );
  }, [prs, search, statusFilter]);
  const pager = usePagination(filtered, 100, `${statusFilter}|${search}`);

  return (
    <>
      <PageIntro
        eyebrow="Procurement · request"
        title="Purchase requests"
        subtitle={`Approval flow: ${flow.length ? flow.map((r) => r.toUpperCase()).join(" → ") : "not configured"}`}
        testid="pr-title"
        action={
          <div className="action-cluster">
            <button
              data-testid="pr-upload-button"
              className="secondary-button"
              onClick={() => setModal("upload")}
            >
              <Upload size={16} /> Upload CSV
            </button>
            <button data-testid="create-pr-button" className="primary-button" onClick={openNew}>
              <Plus size={17} /> New PR
            </button>
          </div>
        }
      />
      <section className="panel">
        <div className="toolbar">
          <div className="search-box">
            <Search size={17} />
            <input
              data-testid="pr-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by PR#, requester, outlet, status..."
            />
          </div>
          {statusFilter && (
            <button
              type="button"
              className="chip small"
              data-testid="pr-clear-status-filter"
              onClick={() => setSearchParams({})}
              title="Hapus filter status"
              style={{ marginLeft: 10 }}
            >
              Filter: {STATUS_LABEL[statusFilter] || statusFilter} <X size={12} />
            </button>
          )}
        </div>
        <PanelHead title="PR list" detail={`${filtered.length} record(s)`} />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>PR#</th>
                <th>Requester</th>
                <th>Outlet</th>
                <th>Items</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Requested</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40 }}>
                    No purchase requests yet.
                  </td>
                </tr>
              )}
              {pager.pageItems.map((p) => (                <tr key={p.id} data-testid={`pr-row-${p.id}`}>
                  <td>
                    <button className="text-button" onClick={() => openDetail(p)} data-testid={`pr-open-${p.id}`}>
                      <strong>{p.pr_number}</strong>
                    </button>
                  </td>
                  <td>{p.requester_name}<small>{p.requester_username}</small></td>
                  <td>{outletNames?.[p.outlet_code || p.department] || (p.outlet_code || p.department)}</td>
                  <td>{p.items?.length || 0}</td>
                  <td><Badge tone={PRIORITY_TONE[p.priority]}>{p.priority}</Badge></td>
                  <td>
                    <Badge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status] || p.status}</Badge>
                    {p.status === "pending_approval" && (
                      <small>Waiting: <b>{currentApproverRole(p)}</b></small>
                    )}
                  </td>
                  <td><small>{formatDate(p.request_date)}</small></td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {canSubmit(p) && (
                      <button data-testid={`pr-submit-${p.id}`} className="small-button success" onClick={() => submit(p)}>
                        <Send size={12} /> Submit
                      </button>
                    )}
                    {canEditPr(p) && (
                      <button data-testid={`pr-edit-${p.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => openEdit(p)}>
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                    {canApprove(p) && (
                      <>
                        <button data-testid={`pr-approve-${p.id}`} className="small-button success" style={{ marginLeft: 6 }} onClick={() => openDecide(p, "approved")}>
                          <Check size={12} /> Approve
                        </button>
                        <button data-testid={`pr-return-${p.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => openDecide(p, "returned")}>
                          <RotateCcw size={12} /> Return
                        </button>
                        <button data-testid={`pr-reject-${p.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => openDecide(p, "rejected")}>
                          <X size={12} /> Reject
                        </button>
                      </>
                    )}
                    {canConvert(p) && (
                      <button data-testid={`pr-convert-${p.id}`} className="small-button success" style={{ marginLeft: 6 }} onClick={() => openConvert(p)}>
                        <Layers size={12} /> To PO
                      </button>
                    )}
                    {canCancel(p) && (
                      <button data-testid={`pr-cancel-${p.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => openCancel(p)}>
                        <X size={12} /> Cancel
                      </button>
                    )}
                    <button data-testid={`pr-print-${p.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => doPrint(p)} title="Print">
                      <Printer size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="pr-pagination" />
      </section>

      {modal === "form" && (
        <Modal
          title={editingId ? "Edit purchase request" : "New purchase request"}
          onClose={() => { setModal(null); setEditingId(null); }}
        >
          <form className="form-grid" onSubmit={save}>
            <label className="field">
              <span>Outlet *</span>
              <select
                data-testid="pr-outlet-select"
                value={form.outlet_code}
                onChange={(e) => setForm({ ...form, outlet_code: e.target.value })}
                required
              >
                <option value="">— Select outlet —</option>
                {outlets.map((o) => (
                  <option key={o.id} value={o.code}>
                    {o.code} — {o.name}
                  </option>
                ))}
              </select>
              {outlets.length === 0 && (
                <small style={{ color: "var(--muted)" }}>
                  No active outlets yet. An admin can add them in <b>System settings</b>.
                </small>
              )}
            </label>
            <Field label="Cost center" testid="pr-cost-center-input" value={form.cost_center} onChange={(v) => setForm({ ...form, cost_center: v })} placeholder="CC-KITCHEN-01" />
            {isRequester && (
              <Field label="Nama Pemesan *" testid="pr-requester-name-input" value={form.requester_name} onChange={(v) => setForm({ ...form, requester_name: v })} placeholder="Nama lengkap pemesan" />
            )}
            <Field label="Required delivery date" testid="pr-delivery-date-input" type="date" value={form.required_delivery_date} onChange={(v) => setForm({ ...form, required_delivery_date: v })} />
            <Field label="Project / Vessel / Location" testid="pr-project-input" value={form.project} onChange={(v) => setForm({ ...form, project: v })} placeholder="e.g. Beach House renovation" />
            <SelectField label="Priority" testid="pr-priority-select" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={["low", "medium", "high", "urgent"]} />
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Remarks / Notes</span>
              <textarea data-testid="pr-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Reason for request, specifications..." rows={2} />
            </label>

            <div style={{ gridColumn: "1/-1" }}>
              <p className="eyebrow">Line items</p>
              <p className="form-hint" style={{ margin: "0 0 8px" }}>
                Price is prefilled from the last purchase but stays editable. Leave blank to
                save a partial draft — approvers or purchasing can fill it in later.
              </p>
              <div className="line-editor">
                {form.items.map((l, idx) => (
                  <div className="line-row" key={idx} style={{ gridTemplateColumns: "2fr 80px 80px 110px 1.4fr 36px" }}>
                    <ItemPicker
                      items={items}
                      value={l.item_id}
                      onChange={(id) => onPickItem(idx, id)}
                      testid={`pr-line-item-${idx}`}
                      placeholder="Search item, or type name below to request new..."
                    />
                    <input
                      data-testid={`pr-line-qty-${idx}`}
                      type="number"
                      min="0"
                      value={l.qty}
                      onChange={(e) => setLine(idx, { qty: e.target.value })}
                      placeholder="Qty"
                    />
                    <input
                      data-testid={`pr-line-unit-${idx}`}
                      value={l.unit}
                      onChange={(e) => setLine(idx, { unit: e.target.value })}
                      placeholder="Unit *"
                      required
                      readOnly={!!l.unit_locked}
                      title={l.unit_locked ? "UOM locked — inherits from the item master" : "Unit of measure (required)"}
                      style={l.unit_locked ? { background: "#f6f8f6", cursor: "not-allowed" } : undefined}
                    />
                    <input
                      data-testid={`pr-line-price-${idx}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.price ?? ""}
                      onChange={(e) => setLine(idx, { price: e.target.value })}
                      placeholder="Last price"
                      title="Unit price hint (defaults to last purchase price, editable)"
                    />
                    <input
                      data-testid={`pr-line-name-${idx}`}
                      value={l.name}
                      onChange={(e) => setLine(idx, { name: e.target.value })}
                      placeholder="Item name (auto from picker, or type manually)"
                    />
                    <button type="button" className="icon-button" onClick={() => removeLine(idx)} data-testid={`pr-line-remove-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button type="button" data-testid="pr-add-line" className="chip small" onClick={addLine}>
                  <Plus size={12} /> Add item
                </button>
              </div>
            </div>

            <div style={{ gridColumn: "1/-1" }}>
              <p className="eyebrow">Attachments (max 5, ≤ 2MB each)</p>
              <label className="attach-drop" data-testid="pr-attach-drop">
                <FileText size={16} /> Choose files (quotation, spec, images)
                <input
                  type="file"
                  multiple
                  hidden
                  data-testid="pr-attach-input"
                  onChange={(e) => onFilesPicked(e.target.files)}
                />
              </label>
              {form.attachments?.length > 0 && (
                <ul className="attach-list">
                  {form.attachments.map((a, i) => (
                    <li key={i} data-testid={`pr-attach-item-${i}`}>
                      <FileText size={14} /> {a.name}
                      <small>{Math.round((a.size || 0) / 1024)} KB</small>
                      <button type="button" className="icon-button" onClick={() => removeAttach(i)}>
                        <Trash2 size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => { setModal(null); setEditingId(null); }}>Cancel</button>
              <button data-testid="pr-save-button" className="primary-button" type="submit" disabled={saving || (isRequester && !(form.requester_name || "").trim())}>
                <Check size={16} /> {saving ? "Saving..." : editingId ? "Update PR" : "Save draft"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "detail" && detail && (
        <Modal title={`PR ${detail.pr_number}`} onClose={() => setModal(null)}>
          <PRDetailView pr={detail} flow={flow} items={items} />
        </Modal>
      )}

      {modal && typeof modal === "object" && modal.decide && (
        <Modal
          title={`${modal.decision === "approved" ? "Approve" : modal.decision === "rejected" ? "Reject" : "Return"} ${modal.pr.pr_number}`}
          onClose={() => setModal(null)}
        >
          <form className="form-grid" onSubmit={submitDecision}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              {modal.decision === "approved"
                ? "Optional comment — will be visible in the PR history."
                : modal.decision === "rejected"
                ? "Rejection is final. Please explain why."
                : "Returning sends the PR back to the requester for revision."}
            </p>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Comment{modal.decision !== "approved" ? " (required)" : ""}</span>
              <textarea
                data-testid="pr-decide-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add remarks..."
                rows={4}
                autoFocus
                required={modal.decision !== "approved"}
              />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button
                data-testid="pr-decide-confirm"
                type="submit"
                disabled={saving}
                className={`primary-button ${modal.decision === "rejected" ? "danger" : ""}`}
              >
                {saving ? "Processing..." : `Confirm ${modal.decision}`}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "convert" && detail && (
        <Modal title={`Convert ${detail.pr_number} to PO(s)`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={submitConvert}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              Supplier &amp; price are pre-filled from the item's <b>last purchase</b> (or the price
              on the PR line). Uncheck any line to skip it — those lines stay <b>outstanding</b> and
              can be converted later. Lines sharing a supplier are merged into one PO.
            </p>
            <div className="table-wrap" style={{ gridColumn: "1/-1", maxHeight: 400, overflow: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <input
                        type="checkbox"
                        data-testid="pr-convert-select-all"
                        checked={convertLines.every((l) => l.converted || l.selected)}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setConvertLines(convertLines.map((l) => l.converted ? l : { ...l, selected: on }));
                        }}
                      />
                    </th>
                    <th>#</th>
                    <th>Item</th>
                    <th>Qty · UOM</th>
                    <th>Supplier</th>
                    <th>Unit price (IDR)</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.items || []).map((it, idx) => {
                    const row = convertLines[idx] || {};
                    const masterItem = items.find((i) => i.id === it.item_id);
                    const lastSupplier = masterItem?.last_supplier;
                    const lastPrice = masterItem?.last_price;
                    if (row.converted) {
                      return (
                        <tr key={idx} data-testid={`pr-convert-row-${idx}`} style={{ opacity: 0.55 }}>
                          <td><small>—</small></td>
                          <td>{idx + 1}</td>
                          <td>
                            <strong>{it.name}</strong>
                            {it.notes && <small>{it.notes}</small>}
                          </td>
                          <td>{it.qty} {it.unit}</td>
                          <td colSpan={2}>
                            <Badge tone="green">Converted → {row.converted_po_number || "PO"}</Badge>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={idx} data-testid={`pr-convert-row-${idx}`}>
                        <td>
                          <input
                            type="checkbox"
                            data-testid={`pr-convert-select-${idx}`}
                            checked={!!row.selected}
                            onChange={(e) => {
                              const next = [...convertLines];
                              next[idx] = { ...next[idx], selected: e.target.checked };
                              setConvertLines(next);
                            }}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td>
                          <strong>{it.name}</strong>
                          {it.notes && <small>{it.notes}</small>}
                        </td>
                        <td>
                          <strong>{it.qty}</strong> <small>{it.unit}</small>
                        </td>
                        <td>
                          <input
                            list={`convert-supplier-list`}
                            data-testid={`pr-convert-supplier-${idx}`}
                            value={row.supplier || ""}
                            onChange={(e) => {
                              const next = [...convertLines];
                              next[idx] = { ...next[idx], supplier: e.target.value };
                              setConvertLines(next);
                            }}
                            placeholder="Vendor name"
                            disabled={!row.selected}
                            style={{ width: "100%", padding: 8, border: "1px solid var(--line)", borderRadius: 5 }}
                          />
                          {lastSupplier && (
                            <small style={{ display: "block", marginTop: 4, color: "var(--muted)" }}>
                              Last: <strong>{lastSupplier}</strong>
                            </small>
                          )}
                        </td>
                        <td>
                          <input
                            data-testid={`pr-convert-price-${idx}`}
                            type="number"
                            min="0"
                            value={row.price || ""}
                            onChange={(e) => {
                              const next = [...convertLines];
                              next[idx] = { ...next[idx], price: e.target.value };
                              setConvertLines(next);
                            }}
                            placeholder="0"
                            disabled={!row.selected}
                            style={{ width: 140, padding: 8, border: "1px solid var(--line)", borderRadius: 5 }}
                          />
                          {lastPrice ? (
                            <small style={{ display: "block", marginTop: 4, color: "var(--muted)" }}>
                              Last: <strong>{new Intl.NumberFormat("en-US", { style: "currency", currency: "IDR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(lastPrice))}</strong>
                            </small>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <datalist id="convert-supplier-list">
                {suppliers.map((s) => (
                  <option key={s.id} value={s.name} />
                ))}
              </datalist>
            </div>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button data-testid="pr-convert-confirm" type="submit" disabled={saving} className="primary-button">
                <Layers size={16} /> {saving ? "Generating..." : "Generate PO(s)"}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {modal === "upload" && (
        <BulkUploadDialog
          title="Upload purchase requests (CSV)"
          endpoint="/purchase-requests/bulk-upload"
          templateName="purchase_request_template.csv"
          templateHeaders={PR_UPLOAD_TEMPLATE.headers}
          templateExample={PR_UPLOAD_TEMPLATE.example}
          instructions={
            <>
              Rows sharing the same <b>pr_ref</b> become one PR (saved as draft). Provide{" "}
              <b>item_sku</b> matching your item master to auto-link the item; otherwise supply{" "}
              <b>item_name</b> as free text. Priority: low / medium / high / urgent.
            </>
          }
          onClose={() => setModal(null)}
          onSuccess={loadAll}
          testid="pr-upload"
        />
      )}

      {modal && typeof modal === "object" && modal.cancel && (
        <Modal title={`Cancel ${modal.pr.pr_number}`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={submitCancel}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              This is an admin override. The PR will be moved to <b>cancelled</b> and cannot be edited or converted.
            </p>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Cancellation reason (required)</span>
              <textarea
                data-testid="pr-cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                autoFocus
                required
              />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Keep PR</button>
              <button data-testid="pr-cancel-confirm" className="primary-button danger" type="submit" disabled={saving}>
                <X size={16} /> {saving ? "Cancelling..." : "Confirm cancel"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function PRDetailView({ pr, flow, items = [] }) {
  const currentApprover = (pr.approval_flow || [])[pr.current_level || 0];
  const money0 = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "IDR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0));
  return (
    <div className="pr-detail">
      <div className="pr-header-grid">
        <div><span className="lbl">Requester</span><strong>{pr.requester_name}</strong><small>@{pr.requester_username}</small></div>
        <div><span className="lbl">Requested</span><strong>{formatDate(pr.request_date)}</strong></div>
        <div><span className="lbl">Outlet</span><strong>{pr.outlet_code || pr.department}</strong></div>
        <div><span className="lbl">Cost center</span><strong>{pr.cost_center || "-"}</strong></div>
        <div><span className="lbl">Required by</span><strong>{pr.required_delivery_date || "-"}</strong></div>
        <div><span className="lbl">Project</span><strong>{pr.project || "-"}</strong></div>
        <div><span className="lbl">Priority</span><Badge tone={PRIORITY_TONE[pr.priority]}>{pr.priority}</Badge></div>
        <div><span className="lbl">Status</span><Badge tone={STATUS_TONE[pr.status]}>{STATUS_LABEL[pr.status]}</Badge></div>
      </div>
      {pr.notes && (
        <div className="pr-notes">
          <span className="lbl">Remarks</span>
          <p>{pr.notes}</p>
        </div>
      )}
      <p className="eyebrow" style={{ marginTop: 16 }}>Line items</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>#</th><th>Item</th><th>Description</th><th>Category</th><th>Qty req.</th><th>Stock on hand</th><th>Price</th><th>PO status</th></tr>
          </thead>
          <tbody>
            {(pr.items || []).map((it, i) => {
              const master = items.find((m) => m.id === it.item_id);
              const stock = master ? Number(master.stock || 0) : null;
              const minStock = master ? Number(master.min_stock || 0) : null;
              const unit = it.unit || (master ? master.unit : "");
              let stockTone = "neutral";
              if (stock !== null) {
                if (stock <= 0) stockTone = "amber";
                else if (minStock !== null && stock < minStock) stockTone = "amber";
                else stockTone = "green";
              }
              return (
                <tr key={i} data-testid={`pr-detail-line-${i}`}>
                  <td>{i + 1}</td>
                  <td><strong>{it.name}</strong></td>
                  <td><small>{it.notes || "-"}</small></td>
                  <td>{it.category || "-"}</td>
                  <td>{it.qty} {it.unit}</td>
                  <td data-testid={`pr-detail-stock-${i}`}>
                    {master ? (
                      <>
                        <Badge tone={stockTone}>{stock} {unit}</Badge>
                        {minStock ? <small>min {minStock}</small> : null}
                      </>
                    ) : (
                      <small>New item · no stock</small>
                    )}
                  </td>
                  <td>{it.price != null && it.price !== "" ? money0(it.price) : "-"}</td>
                  <td>
                    {it.converted ? (
                      <Badge tone="green">
                        {it.converted_po_number || "Converted"}
                      </Badge>
                    ) : (
                      <Badge tone="neutral">Outstanding</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="eyebrow" style={{ marginTop: 16 }}>
        Approval flow — {(pr.approval_flow || flow).map((r) => r.toUpperCase()).join(" → ")}
        {pr.status === "pending_approval" && currentApprover && <span> · <b>waiting on {currentApprover}</b></span>}
      </p>
      <ul className="approval-list">
        {(pr.approvals || []).map((a, i) => (
          <li key={i} data-testid={`pr-approval-log-${i}`}>
            <Badge tone={a.decision === "approved" ? "green" : a.decision === "rejected" ? "neutral" : "amber"}>
              {a.decision}
            </Badge>
            <div>
              <strong>Level {a.level + 1} · {a.role}</strong>
              <small>{a.decided_by} · {formatDate(a.decided_at)}</small>
              {a.comment && <p>“{a.comment}”</p>}
            </div>
          </li>
        ))}
        {(pr.approvals || []).length === 0 && <li className="empty">No approvals yet.</li>}
      </ul>

      {pr.attachments && pr.attachments.length > 0 && (
        <>
          <p className="eyebrow" style={{ marginTop: 16 }}>Attachments</p>
          <div className="attach-preview-grid" data-testid="pr-attachments-list">
            {pr.attachments.map((a, i) => {
              const isImage =
                (a.data || "").startsWith("data:image/") ||
                /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(a.name || "");
              return (
                <div key={i} className="attach-preview" data-testid={`pr-attachment-${i}`}>
                  {isImage ? (
                    <a href={a.data} target="_blank" rel="noreferrer" title="Open full size">
                      <img
                        src={a.data}
                        alt={a.name}
                        data-testid={`pr-attachment-image-${i}`}
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid var(--line)",
                        }}
                      />
                    </a>
                  ) : (
                    <div
                      style={{
                        height: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed var(--line)",
                        borderRadius: 6,
                        color: "var(--muted)",
                      }}
                    >
                      <FileText size={22} />
                    </div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <a href={a.data} download={a.name} target="_blank" rel="noreferrer">
                      {a.name}
                    </a>
                    <small style={{ display: "block", color: "var(--muted)" }}>
                      {Math.round((a.size || 0) / 1024)} KB
                    </small>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {pr.status === "converted" && (pr.converted_po_ids || []).length > 0 && (
        <p className="form-hint" style={{ marginTop: 12 }}>
          Converted into {pr.converted_po_ids.length} PO(s). Check the Purchase Order module.
        </p>
      )}
    </div>
  );
}

function blankLine() {
  return { item_id: "", sku: "", name: "", category: "", qty: "1", unit: "pcs", price: "", notes: "" };
}

function defaultForm() {
  return {
    outlet_code: "",
    cost_center: "",
    requester_name: "",
    required_delivery_date: "",
    project: "",
    priority: "medium",
    notes: "",
    items: [blankLine()],
    attachments: [],
  };
}
